import type { Membership, Tenant } from '../../api/types.js';

// Role badge color per the mockup. Maps the contract enum to one of
// `pill--success|warning|info|neutral`. Mockup `09-admin-members.png`
// shows admin roles in red, AP staff in dark, viewer in grey.
const ROLE_PILL_TONE: Record<Membership['role'], string> = {
  group_admin: 'pill--danger',
  ap_admin: 'pill--danger',
  ap_approver: 'pill--warning',
  ap_clerk: 'pill--info',
  viewer: 'pill--neutral',
};

const ROLE_LABEL_FR_SHORT: Record<Membership['role'], string> = {
  group_admin: 'admin grp',
  ap_admin: 'admin',
  ap_approver: 'validateur',
  ap_clerk: 'comptable',
  viewer: 'lecture',
};

function shortTenant(t: Tenant): string {
  // Reuse the AppShell abbreviation rules so the pill matches the
  // sidebar label visually.
  const n = t.legalName;
  if (/altyor industrie/i.test(n)) return 'AIF';
  if (/altyor holding/i.test(n)) return 'Holding';
  return n;
}

interface PillEntry {
  membership: Membership;
  tenant: Tenant;
}

interface Props {
  entries: PillEntry[];
  // Hook to open the role-change modal for a specific membership when
  // the parent screen wants in-row editing. When omitted, pills render
  // non-interactive (display-only).
  onEditRole?: (entry: PillEntry) => void;
  onRevoke?: (entry: PillEntry) => void;
}

// REQ-FE-029h — One pill per société access for the aggregated user
// row. Each pill shows `{shortTenant} [role]`; clicking opens the
// edit / revoke menu when the parent provides handlers.
export function AccessPills({ entries, onEditRole, onRevoke }: Props) {
  if (entries.length === 0) {
    return <span className="muted">—</span>;
  }
  return (
    <ul className="admin-access-pills" data-testid="admin-access-pills">
      {entries.map((e) => {
        const tone = ROLE_PILL_TONE[e.membership.role];
        const label = `${shortTenant(e.tenant)} · ${ROLE_LABEL_FR_SHORT[e.membership.role]}`;
        // Keep the legacy `admin-row-{uid}-{tid}` testid on the pill so
        // existing tests from REQ-FE-024 that locate a per-membership
        // row keep finding their target after the per-user aggregation.
        const testid = `admin-row-${e.membership.uid}-${e.tenant.tenantId}`;
        return (
          <li key={`${e.membership.uid}-${e.tenant.tenantId}`} className="admin-access-pill-li">
            <span className={`pill ${tone}`} data-testid={testid}>
              {label}
            </span>
            {onEditRole || onRevoke ? (
              <span className="admin-access-pill-actions">
                {onEditRole ? (
                  <button
                    type="button"
                    className="btn-ghost btn-ghost--xs"
                    onClick={() => onEditRole(e)}
                    title="Changer le rôle sur cette société"
                    data-testid={`admin-row-${e.membership.uid}-${e.tenant.tenantId}-edit`}
                  >
                    ✎
                  </button>
                ) : null}
                {onRevoke ? (
                  <button
                    type="button"
                    className="btn-ghost btn-ghost--xs"
                    onClick={() => onRevoke(e)}
                    title="Révoquer ce membre de cette société"
                    data-testid={`admin-row-${e.membership.uid}-${e.tenant.tenantId}-revoke`}
                  >
                    ✕
                  </button>
                ) : null}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
